// (c) chaehongee. Confidential and proprietary.

import { useEffect, useLayoutEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks/hooks'
import { setPositions, type CanvasPosition } from '../../../../store/slices/canvasPositionsSlice'
import { updateCanvasPositions } from '../../../../services/session'

const STORAGE_KEY_PREFIX = 'knoll:canvas:positions'

interface PositionPersistenceOptions {
    enableServerSync?: boolean
}

/**
 * Syncs canvas positions with localStorage for the current session.
 * Loads positions on session change, saves on position changes.
 */
export function usePositionPersistence(
    sessionId: string | null,
    options: PositionPersistenceOptions = {}
) {
    const dispatch = useAppDispatch()
    const positions = useAppSelector((state) =>
        sessionId ? state.canvasPositions.positions[sessionId] : null
    )
    const serverPositions = useAppSelector((state) =>
        sessionId ? state.canvasPositions.serverPositionsBySession[sessionId] : null
    )
    const enableServerSync = options.enableServerSync ?? true
    const skipInitialSyncRef = useRef(true)
    const lastSyncedSnapshotRef = useRef<string | null>(null)
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastSessionIdRef = useRef<string | null>(null)

    // Load positions from localStorage when session changes.
    // Use a layout effect so positions are available for initial layout/hydration
    // before the first paint, avoiding "layout then overwrite" flicker.
    useLayoutEffect(() => {
        if (!sessionId) return
        if (lastSessionIdRef.current !== sessionId) {
            lastSessionIdRef.current = sessionId
            skipInitialSyncRef.current = true
            lastSyncedSnapshotRef.current = null
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
                debounceTimerRef.current = null
            }
        }

        const key = `${STORAGE_KEY_PREFIX}:${sessionId}`
        try {
            const stored = localStorage.getItem(key)
            if (stored) {
                const parsed = JSON.parse(stored) as unknown
                if (parsed && typeof parsed === 'object') {
                    dispatch(setPositions({ sessionId, positions: parsed as any }))
                }
            }
        } catch (error) {
            console.warn('Failed to load canvas positions from localStorage:', error)
        }
    }, [sessionId, dispatch])

    // Save positions to localStorage when they change
    useEffect(() => {
        if (!sessionId || !positions) return

        const key = `${STORAGE_KEY_PREFIX}:${sessionId}`
        try {
            localStorage.setItem(key, JSON.stringify(positions))
        } catch (error) {
            console.warn('Failed to save canvas positions to localStorage:', error)
            // Handle quota exceeded or other localStorage errors
            // Could implement cleanup of old sessions here
        }
    }, [sessionId, positions])

    // Sync positions to backend when enabled
    useEffect(() => {
        if (!enableServerSync || !sessionId || !positions) return
        if (skipInitialSyncRef.current) {
            skipInitialSyncRef.current = false
            lastSyncedSnapshotRef.current = serializePositions(positions)
            return
        }

        const currentSnapshot = serializePositions(positions)
        const serverSnapshot = serverPositions
            ? serializePositions(serverPositions)
            : null
        if (serverSnapshot && currentSnapshot === serverSnapshot) return
        if (lastSyncedSnapshotRef.current === currentSnapshot) return

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            void (async () => {
                try {
                    await updateCanvasPositions(sessionId, {
                        positions: toApiPositions(positions),
                    })
                    lastSyncedSnapshotRef.current = currentSnapshot
                } catch (error) {
                    console.warn('Failed to save canvas positions to server:', error)
                }
            })()
        }, 2000)

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
                debounceTimerRef.current = null
            }
        }
    }, [enableServerSync, sessionId, positions, serverPositions])
}

function toApiPositions(
    positions: Record<string, CanvasPosition>
): Record<
    string,
    { x: number; y: number; w: number; h: number; manually_set: boolean; timestamp: number }
> {
    const mapped: Record<
        string,
        { x: number; y: number; w: number; h: number; manually_set: boolean; timestamp: number }
    > = {}
    for (const [id, pos] of Object.entries(positions)) {
        mapped[id] = {
            x: pos.x,
            y: pos.y,
            w: pos.w,
            h: pos.h,
            manually_set: pos.manuallySet,
            timestamp: pos.timestamp,
        }
    }
    return mapped
}

function serializePositions(positions: Record<string, CanvasPosition>): string {
    const keys = Object.keys(positions).sort()
    const entries = keys.map((key) => [key, positions[key]])
    return JSON.stringify(entries)
}
